import { useMemo, ReactNode } from "react";
import { flagEmoji } from "../../../../helper/common";
import PublicIcon from "@mui/icons-material/Public";
import {
  Icon,
  Notice,
  OptionCard,
  SearchSelect,
} from "../../../../components/design";
import styles from "../authenticateYourId.module.css";

interface SelectOption {
  value: string;
  labelKey: string;
}

interface DocMetaEntry {
  title: string;
  hint: string;
  icon: ReactNode;
}

const DOC_META: Record<string, DocMetaEntry> = {
  national_identity_card: {
    title: "National identity card",
    hint: "frontAndBack",
    icon: <Icon name="idCard" />,
  },
  passport: {
    title: "Passport",
    hint: "photoPage",
    // ponytail: no globe/passport icon in the shared Icon set yet
    icon: <PublicIcon fontSize="small" />,
  },
  residence_permit: {
    title: "Residence permit",
    hint: "frontAndBack",
    icon: <Icon name="home" />,
  },
};

type TranslateFn = (key: string) => string;

interface DocumentSelectScreenProps {
  issuingCountry: string;
  docType: string;
  availableCountries: SelectOption[];
  availableDocs: SelectOption[];
  onCountryChange: (country: string) => void;
  onDocTypeChange: (docType: string) => void;
  onContinue: (docType?: string) => void;
  showRetryMessage: boolean;
  _t: TranslateFn;
}

const formatLabel = (value: string) =>
  value
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const getTranslatedText = (
  _t: TranslateFn,
  key: string,
  fallback: string
): string => {
  const translated = _t(key);
  return translated === key ? fallback : translated;
};

const DocumentSelectScreen = ({
  issuingCountry,
  docType,
  availableCountries,
  availableDocs,
  onCountryChange,
  onDocTypeChange,
  onContinue,
  showRetryMessage,
  _t,
}: DocumentSelectScreenProps) => {
  // the flag goes in the label, so typing a country name still filters
  const countryOptions = useMemo(
    () =>
      availableCountries
        .map((country) => ({
          value: country.value,
          name: getTranslatedText(
            _t,
            country.labelKey,
            formatLabel(country.labelKey)
          ),
        }))
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(({ value, name }) => ({
          value,
          label: `${flagEmoji(value)} ${name}`.trim(),
        })),
    [availableCountries, _t]
  );

  const handleDocumentSelect = (documentValue: string) => {
    onDocTypeChange(documentValue);
    onContinue(documentValue);
  };

  return (
    <div className={`${styles.customKycBody} ${styles.customKycDocumentBody}`}>
      <div className={styles.customKycDocumentIntro}>
        <h3>{_t("chooseYourDocument")}</h3>
        <p>{_t("selectIssuingCountryToSeeWhichDocumentsWeAccept")}</p>
      </div>

      {showRetryMessage && (
        <Notice tone="danger" className={styles.customKycDocumentRetryMessage}>
          {_t(
            "previouslyUploadedDocumentsWereNotAcceptedPleaseTryAgainWithNewDocuments"
          )}
        </Notice>
      )}

      <div className={styles.customKycDocumentForm}>
        <SearchSelect
          id="issuing-country"
          label={_t("issuingCountry")}
          placeholder={_t("selectIssuingCountry")}
          options={countryOptions}
          value={issuingCountry}
          onChange={onCountryChange}
          noOptionsText={_t("noCountriesFound")}
        />

        {countryOptions.some((c) => c.value === issuingCountry) && (
          <div className={styles.customKycDocumentsSection}>
            <h4>{_t("acceptedDocuments")}</h4>
            <div className={`ds-option-list ${styles.customKycDocumentList}`}>
              {availableDocs.map((document) => {
                const meta = DOC_META[document.value] || {
                  title: formatLabel(document.labelKey),
                  hint: "Front and back",
                  icon: <Icon name="idCard" />,
                };

                return (
                  <OptionCard
                    key={document.value}
                    variant={
                      docType === document.value ? "highlight" : "default"
                    }
                    icon={meta.icon}
                    title={getTranslatedText(_t, document.labelKey, meta.title)}
                    description={_t(meta.hint)}
                    onClick={() => handleDocumentSelect(document.value)}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentSelectScreen;
